import { useState } from "react";
import { ROLES } from "@dentacore/shared-types";
import type { CreateStaffMember } from "@dentacore/shared-types";

import { usePhoneCountry } from "../api/use-phone-country";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { PhoneInput } from "../ui/phone-input";
import { roleLabel } from "./people-section";
import styles from "./settings.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * Adding someone to the clinic.
 *
 * **There is no password field, deliberately** (decision I1, STORY-064). Saving
 * creates them pending acceptance and hands back a single-use link the admin
 * gives them; the person chooses their own password. An admin who can type a
 * colleague's password is the practice STORY-008 was written to avoid, and no
 * form here reintroduces it.
 *
 * **No branch memberships here either.** Access is granted on the person's own
 * page, after they exist — a create form that also assigned locations would be
 * two decisions in one dialog, and the second one is easier to make once you
 * can see who you are looking at.
 */
export function StaffForm({
  busy,
  refusal,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  /**
   * The address the last save was refused for, or null.
   *
   * **Not the message** — that is already on screen as a toast. This exists so
   * the field itself can be marked, because a sentence at the top of a form
   * with six inputs does not say which one to fix.
   */
  refusal: string | null;
  onCancel: () => void;
  onSubmit: (body: CreateStaffMember) => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<string>("");
  // The ADMIN job makes an administrator, which only access.assign may create
  // (QF-D5) — so it is not offered to somebody the server would refuse.
  const can = useCan();
  // The WhatsApp assistant is Chatbot's: no number without it (QF-D9, QF-D22).
  const chatbot = useModules().has("CHATBOT");
  const jobs = ROLES.filter((name) => name !== "ADMIN" || can("access.assign"));
  const [phone, setPhone] = useState("");
  const [assistantPhone, setAssistantPhone] = useState("");

  /**
   * **Where a new person's dialling code comes from** (decision D4, item 7).
   *
   * The location being worked in, falling back to the clinic's own country and
   * then to nothing. Editable either way: somebody hired here may perfectly
   * well have a number registered somewhere else, which is exactly why this is
   * a prefill and not a lock.
   */
  const phoneCountry = usePhoneCountry();

  const emailRefused =
    refusal !== null && refusal.trim().toLowerCase() === email.trim().toLowerCase();

  const complete =
    email.trim() !== "" && firstName.trim() !== "" && lastName.trim() !== "" && role !== "";

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        if (!complete) return;
        onSubmit({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role,
          // Omitted rather than sent empty: the server treats an absent phone
          // as absent, and "" would be a value nobody typed.
          ...(phone.trim() === "" ? {} : { phoneE164: phone.trim() }),
          ...(!chatbot || !can("access.assign") || assistantPhone.trim() === ""
            ? {}
            : { assistantPhoneE164: assistantPhone.trim() }),
        });
      }}
    >
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="staff-first">
            First name
            <RequiredMark />
          </label>
          <input
            id="staff-first"
            className={styles.input}
            value={firstName}
            disabled={busy}
            onChange={(event) => setFirstName(event.target.value)}
            data-testid="staff-field-first"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="staff-last">
            Last name
            <RequiredMark />
          </label>
          <input
            id="staff-last"
            className={styles.input}
            value={lastName}
            disabled={busy}
            onChange={(event) => setLastName(event.target.value)}
            data-testid="staff-field-last"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="staff-email">
            Email
            <RequiredMark />
          </label>
          <input
            id="staff-email"
            type="email"
            className={`${styles.input} ${emailRefused ? styles.inputInvalid : ""}`}
            value={email}
            disabled={busy}
            aria-invalid={emailRefused}
            onChange={(event) => setEmail(event.target.value)}
            data-testid="staff-field-email"
          />
          {emailRefused && (
            <span className={styles.fieldError} data-testid="staff-email-taken">
              This address already has an account. Try another.
            </span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="staff-role">
            Role
            <RequiredMark />
          </label>
          <select
            id="staff-role"
            className={styles.input}
            value={role}
            disabled={busy}
            onChange={(event) => setRole(event.target.value)}
            data-testid="staff-field-role"
          >
            <option value="">Choose one</option>
            {jobs.map((name) => (
              <option key={name} value={name}>
                {roleLabel(name)}
              </option>
            ))}
          </select>
        </div>

        {/*
          **The dialling code comes from the location** (item 7): the branch
          being worked in, then the clinic's own country, then nothing. It was
          a bare text box expecting somebody to type `+961` from memory.
        */}
        <div className={styles.spanTwo}>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            defaultCountry={phoneCountry}
            testIdPrefix="staff-field-phone"
            label="Phone"
          />
        </div>

        {/*
          **Optional, and separate from the phone above** (STORY-120). Set by
          the admin creating this account, not by the person themselves — the
          docs/DECISIONS.md *STORY-120 grill* entry names the cost of that.
        */}
        {/* Setting anyone's assistant number is access (AU-D1). */}
        {chatbot && can("access.assign") && (
          <div className={styles.spanTwo}>
            <PhoneInput
              value={assistantPhone}
              onChange={setAssistantPhone}
              defaultCountry={phoneCountry}
              testIdPrefix="staff-field-assistant-phone"
              label="WhatsApp assistant number"
            />
          </div>
        )}
      </div>

      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.primary}
          disabled={busy || !complete}
          data-testid="staff-create"
        >
          Add and make a link
        </button>
        <button type="button" className={styles.ghost} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
